import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Box, Button, Card, CardContent, CardHeader, Flex, Heading, LoadingOrbit, Text, TextField, useToast } from '@chatsdet/ui';
import { CheckCircle, Warning, ArrowClockwise } from '@phosphor-icons/react';
import { API_URL } from '../../../config.js';
import { toApiError } from '../../../lib/api-error';

type OrgDocsSettings = {
  docsUrl: string | null;
};

type DocumentationSectionProps = {
  isAdmin: boolean;
};

type DemoStatus = 'idle' | 'loading' | 'success' | 'error';

export function DocumentationSection({ isAdmin }: DocumentationSectionProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [docsUrlDraft, setDocsUrlDraft] = React.useState('');
  const [demoStatus, setDemoStatus] = React.useState<DemoStatus>('idle');
  const [demoResult, setDemoResult] = React.useState<string>('');
  const [demoError, setDemoError] = React.useState<string>('');

  // Fetch current settings
  const settingsQuery = useQuery({
    queryKey: ['org-docs-settings'],
    queryFn: async (): Promise<{ ok: boolean; data: OrgDocsSettings }> => {
      const response = await fetch(`${API_URL}/org/settings`, { credentials: 'include' });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to load documentation settings');
      }
      return response.json();
    },
  });

  // Initialize draft when data loads
  React.useEffect(() => {
    if (settingsQuery.data?.data) {
      setDocsUrlDraft(settingsQuery.data.data.docsUrl ?? '');
    }
  }, [settingsQuery.data]);

  // Update settings mutation
  const updateMutation = useMutation({
    mutationFn: async (docsUrl: string | null) => {
      const response = await fetch(`${API_URL}/org/settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ docsUrl }),
      });
      if (!response.ok) throw await toApiError(response, 'Failed to update documentation settings');
      const data = await response.json();
      return data as { ok: boolean; data: OrgDocsSettings };
    },
    onSuccess: ({ data }) => {
      setDocsUrlDraft(data.docsUrl ?? '');
      queryClient.invalidateQueries({ queryKey: ['org-docs-settings'] });
    },
  });

  // Run the demo query
  const runDemo = React.useCallback(async () => {
    setDemoStatus('loading');
    setDemoResult('');
    setDemoError('');

    try {
      const response = await fetch(`${API_URL}/org/docs/demo`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to test documentation');
      }

      // Handle streaming response (SSE format)
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let accumulated = '';
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? ''; // Keep incomplete line in buffer

        for (const line of lines) {
          // SSE format: "data: {...}"
          if (line.startsWith('data: ')) {
            const jsonStr = line.slice(6);
            if (jsonStr === '[DONE]') continue;

            try {
              const data = JSON.parse(jsonStr);
              // Text deltas contain the actual response text
              if (data.type === 'text-delta' && typeof data.delta === 'string') {
                accumulated += data.delta;
                setDemoResult(accumulated);
              }
            } catch {
              // Ignore parse errors
            }
          }
        }
      }

      setDemoStatus('success');
    } catch (error) {
      setDemoStatus('error');
      setDemoError(error instanceof Error ? error.message : 'Unknown error');
    }
  }, []);

  // Save and test
  const handleSaveAndTest = async () => {
    const url = docsUrlDraft.trim();
    const normalizedUrl = url.length > 0 ? url : null;

    try {
      await updateMutation.mutateAsync(normalizedUrl);
      toast.success('Documentation URL saved');

      if (normalizedUrl) {
        await runDemo();
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save');
    }
  };

  // Clear URL
  const handleClear = async () => {
    try {
      await updateMutation.mutateAsync(null);
      setDocsUrlDraft('');
      setDemoStatus('idle');
      setDemoResult('');
      setDemoError('');
      toast.success('Documentation URL cleared');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to clear');
    }
  };

  if (settingsQuery.isLoading) {
    return (
      <Card padding="lg">
        <CardContent>
          <Flex align="center" justify="center" className="py-10">
            <LoadingOrbit size="md" />
          </Flex>
        </CardContent>
      </Card>
    );
  }

  if (settingsQuery.isError) {
    return (
      <Card padding="lg">
        <CardContent>
          <Flex direction="column" gap="md" className="py-6">
            <Text className="text-destructive">Failed to load documentation settings.</Text>
            <Flex justify="start">
              <Button variant="outline" onPress={() => settingsQuery.refetch()}>
                Retry
              </Button>
            </Flex>
          </Flex>
        </CardContent>
      </Card>
    );
  }

  const currentUrl = settingsQuery.data?.data?.docsUrl;
  const hasChanges = docsUrlDraft !== (currentUrl ?? '');
  const isValidUrl = docsUrlDraft.length === 0 || isValidHttpUrl(docsUrlDraft);

  return (
    <Box className="space-y-6">
      <Card padding="lg">
        <CardHeader>
          <Box>
            <Heading level={3}>Documentation URL</Heading>
            <Text size="sm" className="text-muted-foreground mt-1">
              Configure your organization&apos;s external documentation URL. AI agents can use this
              to answer questions about your product.
            </Text>
          </Box>
        </CardHeader>
        <CardContent>
          <Flex direction="column" gap="lg">
            <TextField
              label="Documentation URL"
              placeholder="https://docs.example.com"
              value={docsUrlDraft}
              onChange={setDocsUrlDraft}
              isDisabled={!isAdmin || updateMutation.isPending}
              description="Enter the URL of your public documentation"
              errorMessage={!isValidUrl ? 'Please enter a valid URL' : undefined}
            />

            {!isAdmin && (
              <Text size="sm" className="text-muted-foreground">
                Only organization admins can update documentation settings.
              </Text>
            )}

            <Flex gap="sm" justify="end">
              {currentUrl && (
                <Button
                  variant="outline"
                  onPress={handleClear}
                  isDisabled={!isAdmin || updateMutation.isPending}
                >
                  Clear
                </Button>
              )}
              <Button
                onPress={handleSaveAndTest}
                isDisabled={!isAdmin || updateMutation.isPending || !isValidUrl || !hasChanges}
              >
                {updateMutation.isPending ? 'Saving...' : 'Save & Test'}
              </Button>
            </Flex>
          </Flex>
        </CardContent>
      </Card>

      {(demoStatus !== 'idle' || currentUrl) && (
        <Card padding="lg">
          <CardHeader>
            <Flex align="center" justify="between">
              <Box>
                <Heading level={4}>Documentation Test</Heading>
                <Text size="sm" className="text-muted-foreground mt-1">
                  AI-generated summary of your documentation
                </Text>
              </Box>
              {currentUrl && demoStatus !== 'loading' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onPress={runDemo}
                  isDisabled={!currentUrl}
                >
                  <ArrowClockwise size={16} className="mr-1" />
                  Retest
                </Button>
              )}
            </Flex>
          </CardHeader>
          <CardContent>
            {demoStatus === 'loading' && (
              <Flex direction="column" gap="md" align="start">
                <Flex align="center" gap="sm">
                  <LoadingOrbit size="sm" />
                  <Text size="sm" className="text-muted-foreground">
                    Analyzing documentation...
                  </Text>
                </Flex>
                {demoResult && (
                  <Box className="p-4 bg-muted rounded-lg w-full">
                    <Text size="sm" className="whitespace-pre-wrap">
                      {demoResult}
                    </Text>
                  </Box>
                )}
              </Flex>
            )}

            {demoStatus === 'success' && (
              <Flex direction="column" gap="md">
                <Flex align="center" gap="sm" className="text-success">
                  <CheckCircle size={20} weight="fill" />
                  <Text size="sm" weight="medium">
                    Documentation readable
                  </Text>
                </Flex>
                <Box className="p-4 bg-muted rounded-lg">
                  <Text size="sm" className="whitespace-pre-wrap">
                    {demoResult}
                  </Text>
                </Box>
              </Flex>
            )}

            {demoStatus === 'error' && (
              <Flex direction="column" gap="md">
                <Flex align="center" gap="sm" className="text-destructive">
                  <Warning size={20} weight="fill" />
                  <Text size="sm" weight="medium">
                    Failed to read documentation
                  </Text>
                </Flex>
                <Box className="p-4 bg-destructive/10 rounded-lg">
                  <Text size="sm" className="text-destructive">
                    {demoError}
                  </Text>
                </Box>
              </Flex>
            )}

            {demoStatus === 'idle' && currentUrl && (
              <Flex direction="column" gap="md" align="start">
                <Text size="sm" className="text-muted-foreground">
                  Click &quot;Retest&quot; to verify the documentation is readable.
                </Text>
              </Flex>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

function isValidHttpUrl(str: string): boolean {
  try {
    const url = new URL(str);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}
